import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, AlertCircle, UserCheck, UserX, Users } from 'lucide-react';
import { userService, User, UserRole, UserSearchParams } from '../services/userService';
import { alertService } from '../services/alertService';

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<UserRole | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [availableRoles, setAvailableRoles] = useState<UserRole[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, [currentPage, searchTerm, selectedRole, selectedStatus]);

  const loadRoles = async () => {
    try {
      const response = await userService.getRoles();
      if (response.success && response.data) {
        setAvailableRoles(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar roles:', error);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const searchParams: UserSearchParams = {
        page: currentPage - 1, // API usa paginação baseada em 0
        size: usersPerPage,
        search: searchTerm || undefined,
        role: selectedRole !== 'all' ? selectedRole : undefined,
        ativo: selectedStatus === 'all' ? undefined : selectedStatus === 'active'
      };
      
      const response = await userService.getUsers(searchParams);
      
      if (response.success && response.data) {
        console.log('🔍 UsersPage - Dados recebidos do service:', response.data.content.map(u => ({ id: u.id, nome: u.nome, ativo: u.ativo })));
        console.log('🔍 UsersPage - Atualizando estado users com:', response.data.content.length, 'usuários');
        
        // Forçar uma nova referência do array para garantir que o React detecte a mudança
        const newUsers = response.data.content.map(user => ({ ...user }));
        console.log('🔍 UsersPage - Novo array de usuários criado:', newUsers.map(u => ({ id: u.id, nome: u.nome, ativo: u.ativo })));
        
        setUsers(newUsers);
        setTotalUsers(response.data.totalElements);
        setTotalPages(response.data.totalPages);
        console.log('🔍 UsersPage - Estado users atualizado com nova referência');
      } else {
        throw new Error(response.message || 'Erro ao carregar usuários');
      }
    } catch (error: any) {
      console.error('Erro ao carregar usuários:', error);
      setError('Erro ao carregar usuários. Verifique se a API está funcionando.');
      alertService.toast('error', 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  // Filtros já são aplicados no backend, não precisamos filtrar no frontend

  const handleEdit = (user: User) => {
    navigate(`/usuarios/${user.id}`);
  };

  const handleDelete = async (userId: string) => {
    const result = await alertService.confirmDelete(
      'Excluir usuário',
      'Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita!',
      'Sim, excluir',
      'Cancelar'
    );
    
    if (result.isConfirmed) {
      try {
        const response = await userService.deleteUser(userId);
        if (response.success) {
          alertService.toast('success', 'Usuário excluído com sucesso!');
          await loadUsers();
        } else {
          throw new Error(response.message || 'Erro ao excluir usuário');
        }
      } catch (error: any) {
        console.error('Erro ao excluir usuário:', error);
        alertService.toast('error', error.message || 'Erro ao excluir usuário');
      }
    }
  };

  const handleToggleStatus = async (user: User) => {
    console.log('🔍 UsersPage - handleToggleStatus chamado para usuário:', { id: user.id, nome: user.nome, ativo: user.ativo });
    const action = user.ativo ? 'inativar' : 'ativar';
    const confirmMessage = `Tem certeza que deseja ${action} o usuário "${user.nome} ${user.sobrenome}"?`;
    
    const result = await alertService.confirm(
      `${action.charAt(0).toUpperCase() + action.slice(1)} usuário`,
      confirmMessage,
      `Sim, ${action}`,
      'Cancelar'
    );
    
    if (!result.isConfirmed) {
      return;
    }

    try {
      let response;
      if (user.ativo) {
        response = await userService.deactivateUser(user.id!);
      } else {
        response = await userService.activateUser(user.id!);
      }
      
      if (response.success) {
        console.log('🔍 UsersPage - Status alterado com sucesso, recarregando usuários...');
        console.log('🔍 UsersPage - Resposta da API:', response);
        if (response.data) {
          console.log('🔍 UsersPage - Dados do usuário após alteração:', { id: response.data.id, nome: response.data.nome, ativo: response.data.ativo });
        }
        alertService.toast('success', 'Status do usuário alterado com sucesso!');
        console.log('🔍 UsersPage - Iniciando recarregamento dos usuários...');
        await loadUsers();
        console.log('🔍 UsersPage - Recarregamento dos usuários concluído');
      } else {
        throw new Error(response.message || 'Erro ao alterar status do usuário');
      }
    } catch (error: any) {
      console.error('Erro ao alterar status do usuário:', error);
      alertService.toast('error', error.message || 'Erro ao alterar status do usuário');
    }
  };

  const getRoleColor = (roles: UserRole[]) => {
    if (!roles || roles.length === 0) return 'bg-gray-100 text-gray-800';
    const role = roles[0]; // Pega o primeiro role
    switch (role) {
      case 'ADMIN': return 'bg-red-100 text-red-800';
      case 'GERENTE': return 'bg-blue-100 text-blue-800';
      case 'VENDEDOR': return 'bg-green-100 text-green-800';
      case 'OPERADOR': return 'bg-purple-100 text-purple-800';
      case 'ESTOQUISTA': return 'bg-yellow-100 text-yellow-800';
      case 'FINANCEIRO': return 'bg-indigo-100 text-indigo-800';
      case 'COMPRADOR': return 'bg-pink-100 text-pink-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (ativo: boolean) => {
    console.log('🎨 getStatusColor chamado com ativo:', ativo, 'retornando:', ativo ? 'verde (ativo)' : 'vermelho (inativo)');
    return ativo 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getRoleLabel = (roles: UserRole[]) => {
    if (!roles || roles.length === 0) return 'Sem perfil';
    const role = roles[0]; // Pega o primeiro role
    switch (role) {
      case 'ADMIN': return 'Administrador';
      case 'GERENTE': return 'Gerente';
      case 'VENDEDOR': return 'Vendedor';
      case 'OPERADOR': return 'Operador';
      case 'ESTOQUISTA': return 'Estoquista';
      case 'FINANCEIRO': return 'Financeiro';
      case 'COMPRADOR': return 'Comprador';
      default: return role;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <AlertCircle className="h-12 w-12 text-red-500" />
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900">Erro ao carregar dados</h3>
          <p className="text-gray-500 mt-1">{error}</p>
          <button
            onClick={loadUsers}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center">
          <div className="bg-red-100 p-2 sm:p-3 rounded-lg mr-3 sm:mr-4">
            <Users className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Usuários</h1>
            <p className="text-sm sm:text-base text-gray-600">Gerencie os usuários do sistema</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/usuarios/novo')}
          className="inline-flex items-center px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm sm:text-base w-full sm:w-auto justify-center"
        >
          <Plus className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
          Novo Usuário
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col lg:flex-row gap-3 sm:gap-4">
          {/* Busca */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-9 sm:pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm sm:text-base"
              />
            </div>
          </div>

          {/* Filtro por Role */}
          <div className="lg:w-48">
            <select
              value={selectedRole}
              onChange={(e) => {
                setSelectedRole(e.target.value as UserRole | 'all');
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm sm:text-base"
            >
              <option value="all">Todos os perfis</option>
              {availableRoles.map(role => (
                <option key={role} value={role}>{getRoleLabel([role])}</option>
              ))}
            </select>
          </div>

          {/* Filtro por Status */}
          <div className="lg:w-48">
            <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value as 'all' | 'active' | 'inactive');
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm sm:text-base"
            >
              <option value="all">Todos os status</option>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabela - Desktop */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sobrenome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Perfil
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data de Criação
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {user.nome}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {user.sobrenome}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {user.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.roles)}`}>
                      {getRoleLabel(user.roles)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => {
                        console.log('🔍 Clique no status do usuário:', { id: user.id, nome: user.nome, ativo: user.ativo });
                        handleToggleStatus(user);
                      }}
                      className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full cursor-pointer hover:opacity-80 transition-opacity ${getStatusColor(user.ativo)}`}
                      title="Clique para alterar status"
                    >
                      {(() => {
                        console.log('🎨 Renderizando status para usuário:', { id: user.id, nome: user.nome, ativo: user.ativo });
                        return user.ativo ? (
                          <>
                            <UserCheck className="h-3 w-3 mr-1" />
                            Ativo
                          </>
                        ) : (
                          <>
                            <UserX className="h-3 w-3 mr-1" />
                            Inativo
                          </>
                        );
                      })()}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>
                      <div>Criado: {formatDate(user.createdAt)}</div>
                      {user.ultimoLogin && (
                        <div className="text-xs text-gray-400">Último login: {formatDate(user.ultimoLogin)}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleEdit(user)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded transition-colors"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(user.id!)}
                        className="text-red-600 hover:text-red-900 p-1 rounded transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {users.map((user) => (
          <div key={user.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-900">
                  {user.nome} {user.sobrenome}
                </h3>
                <p className="text-xs text-gray-500 mt-1">{user.email}</p>
              </div>
              <div className="flex items-center space-x-2 ml-2">
                <button
                  onClick={() => handleEdit(user)}
                  className="text-blue-600 hover:text-blue-900 p-1 rounded transition-colors"
                  title="Editar"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(user.id!)}
                  className="text-red-600 hover:text-red-900 p-1 rounded transition-colors"
                  title="Excluir"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-gray-500">Perfil:</span>
                <div className="mt-1">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.roles)}`}>
                    {getRoleLabel(user.roles)}
                  </span>
                </div>
              </div>
              <div>
                <span className="text-gray-500">Status:</span>
                <div className="mt-1">
                  <button
                    onClick={() => handleToggleStatus(user)}
                    className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full cursor-pointer hover:opacity-80 transition-opacity ${getStatusColor(user.ativo)}`}
                    title="Clique para alterar status"
                  >
                    {(() => {
                      console.log('🎨 Renderizando status MOBILE para usuário:', { id: user.id, nome: user.nome, ativo: user.ativo });
                      return user.ativo ? (
                        <>
                          <UserCheck className="h-3 w-3 mr-1" />
                          Ativo
                        </>
                      ) : (
                        <>
                          <UserX className="h-3 w-3 mr-1" />
                          Inativo
                        </>
                      );
                    })()}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="text-xs text-gray-500">
                <div>Criado: {formatDate(user.createdAt)}</div>
                {user.ultimoLogin && (
                  <div className="mt-1">Último login: {formatDate(user.ultimoLogin)}</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 sm:px-6 space-y-3 sm:space-y-0">
          {/* Mobile Pagination */}
            <div className="flex w-full justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="text-xs text-gray-700 self-center">
                {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Próximo
              </button>
            </div>
            
            {/* Desktop Pagination */}
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Mostrando <span className="font-medium">{((currentPage - 1) * usersPerPage) + 1}</span> a{' '}
                  <span className="font-medium">{Math.min(currentPage * usersPerPage, totalUsers)}</span> de{' '}
                  <span className="font-medium">{totalUsers}</span> resultados
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let page: number;
                    if (totalPages <= 5) {
                      page = i + 1;
                    } else if (currentPage <= 3) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + i;
                    } else {
                      page = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-red-50 border-red-500 text-red-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>
        )}

      {/* Modal removido: agora a edição/criação é feita na rota dedicada */}
    </div>
  );
};

export default UsersPage;